import { useEffect, useRef, useState } from 'react'
import { LineIcon } from '../../components/LineIcon'
import { DOCUMENT_ACCEPT } from '../../components/Documents/documents'
import { modelWords } from '../../lib/workbench'
import type { ChatTurn } from '../../types/workbench'
import { TurnRow } from './WorkbenchParts'
import { AgentHead, AgentSettingsPanel } from './SalesTeam'

// The conversation — the crafting table itself: what you say moves the diagram and the draft, and what Scout did
// about it shows as chips on the turn. Enter sends, Shift+Enter is a new line. The quick prompts are the five
// things a rep says most. The head names who is connected (the model, by its family and version); when no model
// is configured the box says so in one honest line and stays shut.

export const QUICK_PROMPTS = ['Shorter', 'Too sales-y', 'Lead with how we know each other', 'Add the intro I owe', 'No ask — one question', 'Show me the sources']

/** WHERE TO START (Don, stand-up 2026-10-12: "it was just a little overwhelming … where do I start? … I like the progression, because that's
 *  usually where people get stuck"). An empty conversation offers the ways in Chris named — casual reconnect, find an opportunity,
 *  pick up where we left off, give me an idea — each a message to the Wordsmith; the rep clicks one and the conversation is under way. */
export const STARTERS: ReadonlyArray<{ label: string; say: string }> = [
  { label: 'Casual reconnect', say: 'Casual reconnect: a short, warm note that picks the thread back up — no ask, one question about what they are working on now.' },
  { label: 'Find an opportunity', say: 'Find an opportunity: read what is on file and in the news for them and propose the one thing we could help with right now, then draft around it.' },
  { label: 'Pick up where we left off', say: 'Pick up where we left off: continue from our last exchange — what was promised, what is owed, what comes next.' },
  { label: 'Give me an idea', say: 'Give me an idea: suggest three reasons to write to them this week, with the evidence for each, and draft the strongest.' },
]

/** The head of the conversation: the title and who is on the other end. */
export function ChatHead({ modelConfigured, modelId, onSettings, settingsOpen, busy = false }: { modelConfigured: boolean; modelId?: string | null; onSettings?: () => void; settingsOpen?: boolean; /** a turn is in flight: the Wordsmith's mark kicks into gear */ busy?: boolean }) {
  return (
    <header className="chat-head agent-panel-head">
      {/* the Wordsmith (owner, 2026-10-12): the one agent that writes the email; hover says so, click opens its settings */}
      <h2 className="panel-title"><AgentHead agent="wordsmith" onSettings={onSettings} open={settingsOpen} busy={busy} /></h2>
      <span className={`model-badge ${modelConfigured ? 'on' : 'off'}`} role="status" title={modelConfigured ? `Connected to ${modelId ?? 'a model'}` : 'No model is connected on this instance'}>
        <i className="model-dot" />{modelConfigured ? `Connected · ${modelWords(modelId)}` : 'No model connected'}
      </span>
    </header>
  )
}

/** A file dropped into the conversation, held in the form until Send (2026-10-07): it is stored and read while the rep keeps
 *  typing, and goes with the words it was meant to go with. Nothing is said to Scout until Send. */
export interface PendingAttachment { id: string; name: string; status: 'reading' | 'ready' | 'failed'; error?: string }

export function Chat({ turns, onSend, busy, modelConfigured, modelId, unavailable, nameOf, seed, onAttach, attachments = [], onRemoveAttachment, clearOn, startedAt }: {
  turns: ChatTurn[]; onSend: (text: string) => void; busy: boolean; modelConfigured: boolean
  /** a document dropped into the conversation (REQ-119): stored against what is in focus and held in the form until Send */
  onAttach?: (file: File) => void
  attachments?: PendingAttachment[]
  onRemoveAttachment?: (id: string) => void
  /** changes when a message was accepted: only then is the box cleared, so a refused or failed send keeps the words */
  clearOn?: number
  /** when the message in flight was sent, for the elapsed time on the working line */
  startedAt?: number | null
  /** the controller's resolved model id, for the head */
  modelId?: string | null
  /** the last POST was refused: the reason, shown in place of the box */
  unavailable?: string | null
  nameOf?: (id: string) => string
  /** words put into the box from outside (a shape's "Incorporate into draft" — REQ-103); the rep edits and sends them */
  seed?: { text: string; nonce: number } | null
}) {
  const [text, setText] = useState('')
  // the Wordsmith's settings for this sitting (owner, 2026-10-12): opened from its mark in the head
  const [settings, setSettings] = useState(false)
  const listRef = useRef<HTMLOListElement>(null)
  const boxRef = useRef<HTMLTextAreaElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  useEffect(() => { const el = listRef.current; if (el) el.scrollTop = el.scrollHeight }, [turns.length, busy])
  useEffect(() => { if (seed) { setText(seed.text); boxRef.current?.focus() } }, [seed])
  useEffect(() => { if (clearOn) setText('') }, [clearOn])
  const reading = attachments.some((a) => a.status === 'reading')
  const ready = attachments.filter((a) => a.status === 'ready')
  // a message may be the words, the files, or both; it waits for a file still being read
  const send = (t: string) => { const v = t.trim(); if ((!v && ready.length === 0) || busy || reading || !modelConfigured) return; onSend(v) }
  const shut = !modelConfigured || !!unavailable
  const [elapsed, setElapsed] = useState(0)
  useEffect(() => {
    if (!busy || !startedAt) { setElapsed(0); return }
    const id = setInterval(() => setElapsed(Math.round((Date.now() - startedAt) / 1000)), 1000)
    return () => clearInterval(id)
  }, [busy, startedAt])

  return (
    <div className="wb-chat">
      <ChatHead modelConfigured={modelConfigured} modelId={modelId} onSettings={() => setSettings((v) => !v)} settingsOpen={settings} busy={busy} />
      {settings && <AgentSettingsPanel agent="wordsmith" onClose={() => setSettings(false)} />}
      <ol ref={listRef} className="plain turns chat-thread" aria-live="polite">
        {/* the empty conversation opens with the Wordsmith's own message, as a chat does — never a box that looks like somewhere to type */}
        {turns.length === 0 && <li className="turn scout turn-empty"><div className="turn-bubble"><p>Tell me what to change in the email beside us, or what you know. I answer from what is on file, say where each fact came from, and the draft follows.</p></div><div className="turn-meta"><span className="muted small">Wordsmith</span></div></li>}
        {turns.map((t) => <TurnRow key={t.seq} turn={t} nameOf={nameOf} />)}
        {busy && <li className="turn scout thinking" role="status"><div className="turn-bubble"><p className="muted">The Wordsmith is working{elapsed > 0 ? ` · ${elapsed}s` : '…'}<span className="muted small"> — the answer lands here and the draft follows; you can keep typing.</span></p></div></li>}
      </ol>
      {turns.length === 0 && (
        <div className="quick-prompts starters" aria-label="Where to start">
          {STARTERS.map((s) => <button key={s.label} type="button" className="chip-filter starter" onClick={() => send(s.say)} disabled={busy || shut} title={s.say}>{s.label}</button>)}
        </div>
      )}
      <div className="quick-prompts">
        {QUICK_PROMPTS.map((p) => <button key={p} type="button" className="chip-filter" onClick={() => send(p)} disabled={busy || shut}>{p}</button>)}
      </div>
      {shut ? (
        <p className="muted small model-off" role="status">{unavailable ? `model unavailable — ${unavailable}` : 'The conversation needs the CLI controller or an API model; none is configured. The diagram and the mail history are still yours to read.'}</p>
      ) : (
        <form className="chat-box" onSubmit={(e) => { e.preventDefault(); send(text) }}
          onDragOver={(e) => { if (onAttach) e.preventDefault() }} onDrop={(e) => { const f = e.dataTransfer.files?.[0]; if (f && onAttach) { e.preventDefault(); onAttach(f) } }}>
          {attachments.length > 0 && (
            <ul className="plain chat-attachments" aria-label="Files going with this message">
              {attachments.map((a) => (
                <li key={a.id} className={`chat-attachment ${a.status}`} title={a.error ?? (a.status === 'reading' ? 'Being stored and read; it goes with the message when you send' : 'Read; it goes with the message when you send')}>
                  <LineIcon name="paperclip" size={13} />
                  <span className="chat-attachment-name">{a.name}</span>
                  <span className="muted small">{a.status === 'reading' ? 'reading…' : a.status === 'failed' ? `could not be read${a.error ? `: ${a.error}` : ''}` : 'ready'}</span>
                  {onRemoveAttachment && <button type="button" className="button icon small" onClick={() => onRemoveAttachment(a.id)} aria-label={`Remove ${a.name}`}>×</button>}
                </li>
              ))}
            </ul>
          )}
          <textarea ref={boxRef} value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder={attachments.length > 0 ? 'Say what to do with the file, then tell the Wordsmith…' : 'Tell the Wordsmith what to change, or what you know…'} aria-label="Your message"
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(text) } }} />
          <div className="chat-send">
            {onAttach && (
              <>
                <input ref={fileRef} type="file" accept={DOCUMENT_ACCEPT} hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) onAttach(f); e.currentTarget.value = '' }} />
                <button type="button" className="button icon chat-attach" onClick={() => fileRef.current?.click()} disabled={busy} aria-label="Attach a document" title="Attach a document — a deck, a sheet, a proposal. Scout reads it and the conversation uses what it says.">
                  <LineIcon name="paperclip" size={15} />
                </button>
              </>
            )}
            {/* not "Send" (Don, stand-up 2026-10-14: "I'm not going to inadvertently send an email from here?"): this goes to the Wordsmith; mail leaves only from the Outbox */}
            <button type="submit" className="button primary" disabled={busy || reading || (!text.trim() && ready.length === 0)} title="Goes to the Wordsmith only. Nothing mails from here; mail leaves from your Outbox when you send it there.">{busy ? 'Working…' : reading ? 'Reading the file…' : 'Tell the Wordsmith'}</button>
          </div>
        </form>
      )}
    </div>
  )
}
